import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Award, 
  CheckCircle,
  Users,
  Clock,
  Star
} from 'lucide-react';

const IMAGES = {
  // New user-provided vehicle images
  heroMain: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/7mbofut4_1.jpg',
  fleetLineup: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/pxgayyb3_4.jpg',
  fleetWaterview: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/1mru6jql_2.jpg',
  escaladeExterior: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/zdycqsl8_IMG_9182.jpg',
  escaladeInterior: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/fztrwjm3_my26-escalade-gallery-interior-modal-steering-wheel-v2.jpeg',
  
  // Original user images
  escaladeMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg',
  dcMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/t8s41v2p_2.jpg',
  
  // DC/Government images
  whiteHouse: 'https://images.unsplash.com/photo-1570492903043-3d30df7133d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxXYXNoaW5ndG9uJTIwREMlMjBXaGl0ZSUyMEhvdXNlJTIwZ292ZXJubWVudCUyMGV4ZWN1dGl2ZXxlbnwwfHx8fDE3NzM2OTA5NDB8MA&ixlib=rb-4.1.0&q=85',
  capitol: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/7mbofut4_1.jpg',
};

const certifications = [
  {
    icon: Shield,
    title: 'VA DMV Intrastate Operating Authority Certified',
    description: 'Licensed to operate commercial transportation services within Virginia.'
  },
  {
    icon: Award,
    title: 'Washington Metropolitan Area Transit Commission Certified',
    description: 'Authorized to provide passenger transportation in the DC metropolitan area.'
  },
  {
    icon: CheckCircle,
    title: 'Metropolitan Washington Airport Authority Licensed',
    description: 'Permitted to operate at all major airports in the Washington DC region.'
  },
  {
    icon: Shield,
    title: 'DOT Commercial Insurance Requirements',
    description: 'Full compliance with Department of Transportation insurance standards.'
  },
  {
    icon: Award,
    title: 'US DOT Certified',
    description: 'Compliant with federal and state transportation safety laws and regulations.'
  }
];

const values = [
  {
    icon: Star,
    title: 'Excellence',
    description: 'We strive for perfection in every aspect of our service, from vehicle presentation to client communication.'
  },
  {
    icon: Shield,
    title: 'Reliability',
    description: 'Our clients count on us for punctual, dependable transportation—every time, without exception.'
  },
  {
    icon: Users,
    title: 'Professionalism',
    description: 'Our chauffeurs are trained to provide discreet, courteous service that meets executive standards.'
  },
  {
    icon: Clock,
    title: 'Attention to Detail',
    description: 'From flight tracking to vehicle cleanliness, we manage every detail so you can focus on what matters.'
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={IMAGES.dcMonument}
            alt="Washington DC Monument"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block fade-in">
            About Us
          </span>
          <h1 
            data-testid="about-headline"
            className="text-hero font-serif text-white mb-6 max-w-3xl fade-in-delay-1"
          >
            About Capital Chauffeur Services
          </h1>
          <p className="text-[#A3A3A3] text-lg lg:text-xl max-w-2xl leading-relaxed fade-in-delay-2">
            Setting the standard for luxury ground transportation in the 
            Washington DC metropolitan area.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
                Our Mission
              </span>
              <h2 className="text-section font-serif text-white mb-6">
                Defining Luxury Transportation
              </h2>
              <div className="space-y-6 text-[#A3A3A3] leading-relaxed">
                <p>
                  At Capital Chauffeur Services, we believe luxury transportation should be 
                  defined by reliability, professionalism, and attention to detail. Our mission 
                  is simple: deliver a first-class travel experience for every client, every time.
                </p>
                <p>
                  We are a professional black car service dedicated to providing premium 
                  transportation for executives, travelers, and individuals who expect a 
                  higher standard of service. With a growing team of experienced chauffeurs 
                  and a fleet of luxury vehicles, we proudly serve clients who value 
                  punctuality, discretion, and comfort.
                </p>
                <p>
                  Our chauffeurs are carefully selected professionals who understand that 
                  exceptional service goes beyond driving. From the moment your reservation 
                  is confirmed to the moment you arrive at your destination, every detail 
                  is handled with care.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/kyn2ki0d_image1.jpeg"
                  alt="Capital Chauffeur Services Mixed Fleet"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Gold Frame */}
              <div className="absolute -bottom-6 -left-6 w-full h-full border-2 border-[#D4AF37]/30 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Our Fleet
            </span>
            <h2 className="text-section font-serif text-white mb-6">
              Premium Vehicles
            </h2>
          </div>

          {/* Fleet Lineup */}
          <div className="relative mb-12">
            <div className="aspect-[21/9] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/sih2jxml_image0.jpeg"
                alt="Capital Chauffeur Services Fleet Lineup"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Vehicle Images Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="aspect-video overflow-hidden">
              <img
                src={IMAGES.fleetWaterview}
                alt="Escalades with Professional Chauffeur"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-video overflow-hidden">
              <img
                src={IMAGES.escaladeInterior}
                alt="Cadillac Escalade Interior"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Our Values
            </span>
            <h2 className="text-section font-serif text-white mb-6">
              What Sets Us Apart
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={value.title}
                data-testid={`value-card-${index}`}
                className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors group"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37]/30 mb-6 group-hover:bg-[#D4AF37]/10 transition-colors">
                  <value.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-xl text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Washington DC Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image Side */}
            <div className="relative order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={IMAGES.whiteHouse}
                    alt="The White House"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[3/4] overflow-hidden mt-8">
                  <img
                    src={IMAGES.capitol}
                    alt="US Capitol Building"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
                What We Do
              </span>
              <h2 className="text-section font-serif text-white mb-6">
                Serving the Nation&apos;s Capital
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">
                We specialize in providing premium ground transportation services 
                tailored to the unique needs of Washington DC&apos;s elite clientele:
              </p>

              <div className="space-y-4">
                {[
                  'Airport transfers with flight tracking',
                  'Corporate transportation for executives',
                  'Government and diplomatic transport',
                  'Hourly chauffeur service for flexible travel',
                  'Special event transportation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-[#D4AF37]"></div>
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/services">
                  <Button 
                    data-testid="about-services-btn"
                    className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase"
                  >
                    View Our Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section data-testid="certifications-section" className="section-padding bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
              <Shield className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs text-[#D4AF37] tracking-wider uppercase">
                Fully Compliant
              </span>
            </div>
            <h2 className="text-section font-serif text-white mb-6">
              Licensed, Insured & Certified
            </h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto text-lg leading-relaxed">
              We maintain full compliance with all federal, state, and local 
              transportation regulations to ensure your safety and peace of mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div 
                key={cert.title}
                data-testid={`cert-${index}`}
                className="p-6 bg-[#0A0A0A] border border-white/5 flex items-start gap-4"
              >
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]">
                  <cert.icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2 text-sm">
                    {cert.title}
                  </h4>
                  <p className="text-[#A3A3A3] text-xs leading-relaxed">
                    {cert.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroMain}
            alt=""
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-section font-serif text-white mb-6">
            Experience the Difference
          </h2>
          <p className="text-[#A3A3A3] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Discover why executives, government officials, and discerning 
            travelers choose Capital Chauffeur Services for their transportation needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button 
                data-testid="about-cta-book-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Book Your Ride
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
